import { createRouter, RouteRecordRaw, createWebHistory } from "vue-router";
import store from "@/store";
import { Mutations, Actions } from "@/store/enums/StoreEnums";
import posthog from '@/posthog';
import { certificationGuard } from "@/router/guards/certificationGuard";
import { luxGuard } from "@/router/guards/luxGuard";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/dashboard",
    component: () => import("@/components/page-layouts/Auth.vue"),
    children: [
      {
        path: "/sign-in",
        name: "sign-in",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/SignIn.vue"),
      },
      {
        path: "/sign-up",
        name: "sign-up",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/SignUp.vue"),
      },
      {
        path: "/password-reset",
        name: "password-reset",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/PasswordReset.vue"),
      },
      {
        path: "/password/reset",
        name: "password-reset-validation",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/PasswordReset-Validation.vue")
      },
      {
        path: "/auth/email/verify",
        name: "verify-email",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/VerifyEmail.vue"),
      },
      {
        path: "/oauth/:provider",
        name: "oauth-provider",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/Oauth.vue"),
        props: (route) => ({ provider: route.params.provider }),
      },
      {
        path: "/auth/:provider/callback",
        name: "oauth-provider-callback",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/Oauth-Callback.vue"),
        props: (route) => ({ provider: route.params.provider }),
      },
      {
        path: "/privacy-and-terms",
        name: "privacy-and-terms",
        component: () => import("@/views/crafted/privacy/Index.vue"),
      }
    ],
  },
  {
    path: "/",
    redirect: "/dashboard",
    component: () => import("@/layout/Layout.vue"),
    children: [
      // {
      //   path: "/dashboard",
      //   name: "dashboard",
      //   component: () => import("@/views/crafted/course/base/Dashboard.vue"),
      // },
      {
        path: "/dashboard",
        name: "dashboard",
        component: () => import("@/modern/views/Dashboard.vue"),
      },
    ],
  },
  {
    path: "/",
    redirect: "/starting-point/getting-started",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/starting-point/getting-started",
        name: "getting-started",
        component: () => import("@/views/Dashboard.vue"),
      },
    ],
  },
  {
    path: "/",
    redirect: "/laboratory/",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/laboratory/competition-machines",
        name: "competition-machines",
        component: () =>
          import("@/views/crafted/championship/machines/MachinesList.vue"),
      },
      {
        path: "/laboratory/competition-machines/:machine_uuid",
        name: "competition-machines-show",
        component: () =>
          import("@/views/crafted/championship/machines/MachineShow.vue"),
        props: (route) => ({ uuid: route.params.machine_uuid }),
      },
      {
        path: "/laboratory/competition-challenges",
        name: "competition-challenges",
        component: () =>
          import("@/views/crafted/championship/challenges/ChallengesList.vue"),
      },
    ],
  },
  {
    path: "/",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/groups/me",
        name: "groups-me",
        component: () => import("@/views/groups/MyGroup.vue"),
      },
      {
        path: "/groups/create",
        name: "groups-create",
        component: () => import("@/views/groups/CreateGroup.vue"),
      },
      {
        path: "/groups/ranking",
        name: "groups-ranking",
        component: () => import("@/views/groups/GroupRanking.vue"),
      },
      {
        path: "/groups/:id",
        name: "groups-show",
        component: () => import("@/views/groups/GroupProfile.vue"),
        props: (route) => ({ id: route.params.id }),
      },
    ],
  },
  {
    path: "/",
    redirect: "/training/",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/training/learning-paths",
        name: "learning-paths",
        component: () =>
          import("@/views/crafted/training/learning-paths/List.vue"),
      },
      {
        path: "/training/learning-paths/:id",
        name: "learning-paths-show",
        component: () =>
          import("@/views/crafted/training/learning-paths/Show.vue"),
        props: (route) => ({ id: route.params.id }),
      },
      {
        path: "/training/learning-paths/:id/lesson/:lesson_id",
        name: "learning-paths-lesson-show",
        component: () =>
          import("@/views/crafted/training/learning-paths/Lesson/LessonShow.vue"),
        props: (route) => ({ id: route.params.id, lesson_id: route.params.lesson_id }),
      },
      {
        path: "/training/learning-paths/:id/machine/:machine_id",
        name: "learning-paths-machine-show",
        component: () =>
          import("@/views/crafted/training/learning-paths/Machine/MachineShow.vue"),
        props: (route) => ({ id: route.params.id, machine_id: route.params.machine_id }),
      },
      {
        path: "/training/learning-paths/:id/challenge/:challenge_id",
        name: "learning-paths-challenge-show",
        component: () =>
          import("@/views/crafted/training/learning-paths/Challenge/ChallengeShow.vue"),
        props: (route) => ({ id: route.params.id, challenge_id: route.params.challenge_id }),
      },
      {
        path: "/training/training-machines",
        name: "training-machines",
        component: () =>
          import("@/views/crafted/training/machines/MachinesList.vue"),
      },
      {
        path: "/training/training-machines/:machine_uuid",
        name: "training-machines-show",
        component: () =>
          import("@/views/crafted/training/machines/MachineShow.vue"),
        props: (route) => ({ uuid: route.params.machine_uuid }),
      },
      {
        path: "/training/course-catalog",
        name: "course-catalog",
        component: () =>
          import("@/views/crafted/course/base/Catalog.vue"),
      },
      {
        path: "/training/course/:id",
        name: "training-module-list",
        component: () =>
            import("@/views/crafted/course/module/ModuleList.vue"),
        props: (route) => ({ id: route.params.id }),
      },
      {
        path: "/training/modules/:id",
        name: "training-module-show",
        component: () =>
          import("@/views/crafted/course/module/ModuleShow.vue"),
        props: (route) => ({ id: route.params.id }),
      },
      {
        path: "/training/modules/:id/lesson/:lesson_id",
        name: "training-module-show-lesson",
        component: () =>
            import("@/views/crafted/course/module/ModuleShow.vue"),
        props: (route) => ({ id: route.params.id, lesson_id: route.params.lesson_id }),
      },
      {
        path: "/training/challenges",
        name: "training-challenges-list",
        component: () =>
          import("@/views/crafted/training/challenges/ChallengesList.vue")
      },
      {
        path: "/training/challenges/:id",
        name: "training-challenges-show",
        component: () =>
          import("@/views/crafted/training/challenges/ChallengeShow.vue"),
        props: (route)=>({id:route.params.id})
      },
    ],
  },
  {
    path: "/",
    redirect: "/profile/",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/profile/overview",
        name: "profile-overview",
        component: () => import("@/views/crafted/account/Account.vue"),
      },
      {
        path: "/profile/certificate",
        name: "profile-certificate",
        component: () => import("@/views/crafted/account/Certificate.vue"),
      },
      {
        path: "/profile/settings",
        name: "profile-settings",
        component: () =>
          import("@/views/crafted/account/Settings.vue"),
        props: (route) => ({ uuid: route.params.machine_uuid }),
      },
      {
        path: "/profile/billing",
        name: "profile-billing",
        component: () => {}
      },
      {
        path: "/checkout/waiting-payment",
        name: "checkout-waiting-payment",
        component: () =>
          import("@/views/crafted/checkout/Waiting.vue"),
      },
      {
        path: "/profile/billing/renew-subscription",
        name: "renew-subscription",
        component: () =>
          import("@/views/crafted/account/RenewSubscription.vue"),
      }
    ],
  },
  {
    path: "/",
    redirect: "/apps/",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/apps/vpn",
        name: "vpn-route",
        component: () => import("@/views/apps/vpn/core.vue"),
      },
      {
        path: "/apps/global-search",
        name: "global-search",
        component: () => import("@/views/apps/global-search/search.vue"),
      },
      {
        path: "/apps/quimerax-news",
        name: "quimerax-news",
        component: () => import("@/views/apps/quimerax-news/news.vue"),
      },
      {
        path: "/apps/quimerax-news/:uuid",
        name: "quimerax-news-case",
        component: () => import("@/views/apps/quimerax-news/case-detail.vue"),
      },
      {
        // Contexto e conversa chegam por query string:
        // ?context=machine:42 abre uma conversa nova sobre aquela máquina,
        // ?conversation=<uuid> reabre uma existente.
        path: "/apps/lux",
        name: "lux",
        component: () => import("@/views/apps/lux/index.vue"),
        beforeEnter: luxGuard,
      }
    ],
  },
  {
    path: "/",
    redirect: "/club/",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/club/leaderboard",
        name: "leaderboard-route",
        component: () => import("@/views/apps/leaderboard/core.vue")
      },
      {
        path: "/club/leaderboard/:tournament_id",
        name: "tournament-leaderboard",
        component: () => import("@/views/apps/leaderboard/core.vue"),
        props: (route) => ({ tournament_id: route.params.tournament_id }),
        beforeEnter: (to, from, next) => {
          if (to.params.tournament_id === '1') {
            next({ name: 'leaderboard-route' });
          } else {
            next();
          }
        }
      },
      {
        path: "/training/carnavown",
        name: "carnavown-challenges",
        component: () => import("@/views/crafted/training/challenges/TournamentChallengesList.vue"),
        props: () => ({ tournament_id: '3' })
      },
      {
        path: "/club/feed",
        name: "feed",
        component: () => import("@/views/apps/feed/core.vue"),
      },
      {
        path: "/club/feed/:id",
        name: "feed-view",
        component: () => import("@/views/apps/feed/feed-view.vue"),
        props: (route) => ({ id: route.params.id }),
      }
    ],
  },
  {
    path: "/",
    redirect: "/certificate/",
    component: () => import("@/layout/LayoutCertificate.vue"),
    children: [
      {
        path: "/certificate/validate",
        name: "certificate",
        component: () => import("@/views/crafted/certificate/CertificateIndex.vue"),
      },
      {
        path: "/certificate/:id",
        name: "certificate-view",
        component: () => import("@/views/crafted/certificate/CertificateView.vue"),
        props: (route) => ({ id: route.params.id }),
      }

    ],
  },
  {
    path: "/",
    redirect: "/certifications/",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/certifications",
        name: "certifications",
        component: () => import("@/views/crafted/certification/CertificationIndex.vue"),
      },
      {
        path: "/certifications/:id",
        name: "certifications-detail",
        component: () => import("@/views/crafted/certification/CertificationShow.vue"),
        beforeEnter: certificationGuard,
      },
      {
        path: "/certifications/:id/to-do-start",
        name: "certifications-to-do-start",
        component: () => import("@/views/crafted/certification/CertificationToDoStart.vue"),
        props: (route) => ({ id: route.params.id }),
        beforeEnter: certificationGuard,
      },
      {
        path: "/certifications/:id/to-do",
        name: "certifications-to-do",
        component: () => import("@/views/crafted/certification/CertificationToDo.vue"),
        props: (route) => ({ id: route.params.id }),
        beforeEnter: certificationGuard,
      },
      {
        path: "/certifications/:id/modeling",
        name: "certifications-modeling",
        component: () => import("@/views/crafted/certification/CertificationModeling.vue"),
        props: (route) => ({ id: route.params.id }),
        beforeEnter: certificationGuard,
      },
      {
        path: "/certifications/:id/sending-report",
        name: "certifications-sending-report",
        component: () => import("@/views/crafted/certification/CertificationSendingReport.vue"),
        props: (route) => ({ id: route.params.id }),
        beforeEnter: certificationGuard,
      },
      {
        path: "/certifications/:id/feedback",
        name: "certifications-feedback",
        component: () => import("@/views/crafted/certification/CertificationFeedback.vue"),
        props: (route) => ({ id: route.params.id }),
        beforeEnter: certificationGuard,
      },
      {
        path: "/certifications/:certification_id/machines/:id",
        name: "certifications-machines-show",
        component: () =>
            import("@/views/crafted/certification/machines/MachineShow.vue"),
        props: (route) => ({ uuid: route.params.id, certificationId: route.params.certification_id }),
      },
    ]
  },
  {
    path: "/",
    redirect: "/profile/",
    component: () => import("@/layout/LayoutPublic.vue"),
    children: [
      {
        path: "/profile/user/:id",
        name: "profile-user",
        component: () => import("@/views/crafted/profile/User.vue"),
        props: (route) => ({ id: route.params.id })
      },
      {
        path: "/profile/user/:id/badges",
        name: "public-badges",
        component: () => import("@/views/crafted/profile/PublicBadges.vue"),
        props: (route) => ({ id: route.params.id })
      },
    ],
  },
  {
    path: "/",
    redirect: "/podium/",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/podium",
        name: "podium",
        component: () => import("@/views/crafted/podium/PodiumIndex.vue"),
      }
    ]
  },
  {
    // the 404 route, when none of the above matches
    path: "/404",
    name: "404",
    component: () => import("@/views/crafted/authentication/Error404.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((ctx) => {


  const whiteListRoutes = [
    "sign-in",
    "sign-up",
    "password-reset",
    "oauth-provider",
    "oauth-provider-callback",
    "privacy-and-terms",
    "verify-email",
    "password-reset-validation",
    "certificate",
    "certificate-view",
    "profile-user",
    "public-badges"
  ];

  const currentRoutename = <string>ctx.name;

  if (!whiteListRoutes.includes(currentRoutename)) {
    posthog.capture('attempted_protected_route', {
      route: currentRoutename,
    });
    store.dispatch(Actions.VERIFY_AUTH);
    
  }

  // if access if for /profile/billing
  //redirect to "VUE_APP_CHECKOUT_URL/billing";

  if (currentRoutename === "profile-billing") {
    
    window.location.href = process.env.VUE_APP_CHECKOUT_URL + "/billing";
  }

  // reset config to initial state
  store.commit(Mutations.RESET_LAYOUT_CONFIG);



  // Scroll page to top on every route change
  setTimeout(() => {
    window.scrollTo(0, 0);
  }, 100);
});

router.afterEach((to, from) => {
  posthog.capture('pageview', {
    path: to.path,
    name: to.name,
  });
});


export default router;
